import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { logDebug, logError, logInfo, logWarn } from '../utils/safeLog';
 
const WSContext = createContext(null);
 
export const useSocket = () => useContext(WSContext);
 
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || '')
  .replace(/^https:\/\//, 'wss://')
  .replace(/^http:\/\//, 'ws://');
 
const RECONNECT_DELAY = 3000;
const MAX_RECONNECTS = 8;
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
 
export const SocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef(null);
  const listenerMapRef = useRef({});       // type → Set<handler>
  const shouldConnectRef = useRef(false);
  const lastPongRef = useRef(Date.now());
 
  const [connected, setConnected] = useState(false);
  const [wsInstance, setWsInstance] = useState(null);
 
  // Register/unregister event handlers by message type
  const on = useCallback((type, handler) => {
    if (!listenerMapRef.current[type]) listenerMapRef.current[type] = new Set();
    listenerMapRef.current[type].add(handler);
    return () => listenerMapRef.current[type]?.delete(handler);
  }, []);
 
  const off = useCallback((type, handler) => {
    listenerMapRef.current[type]?.delete(handler);
  }, []);
 
  // Emit a typed JSON message to the server
  const emit = useCallback((type, data = {}) => {
    const ws = wsRef.current;
    if (!ws) {
      logWarn('[WS]', 'emit called but ws is null');
      return false;
    }
    if (ws.readyState !== WebSocket.OPEN) {
      logWarn('[WS]', `emit called but ws not open, state: ${ws.readyState}`);
      return false;
    }
    try {
      ws.send(JSON.stringify({ type, ...data }));
      logDebug('[WS] Emitted:', type);
      return true;
    } catch (e) {
      logError('[WS] emit failed', e);
      return false;
    }
  }, []);
 
  // Check if token needs refresh
  const getValidToken = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
 
    try {
      // Parse JWT to check expiry
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expiresIn = tokenData.exp * 1000 - Date.now();
 
      logInfo('[WS] Token expires in:', Math.floor(expiresIn / 1000), 'seconds');
 
      // If token expires soon, try to refresh
      if (expiresIn < TOKEN_REFRESH_THRESHOLD) {
        logInfo('[WS] Token expiring soon, attempting refresh...');
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          try {
            const response = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken })
            });
 
            if (response.ok) {
              const data = await response.json();
              localStorage.setItem('access_token', data.access_token);
              logInfo('[WS] Token refreshed successfully');
              return data.access_token;
            } else {
              logError('[WS] Token refresh failed', { response: { status: response.status } });
            }
          } catch (e) {
            logError('[WS] Token refresh error', e);
          }
        }
      }
      return token;
    } catch (e) {
      logError('[WS] Token validation error', e);
      return token;
    }
  }, []);
 
  const _connect = useCallback(async () => {
    if (!shouldConnectRef.current) {
      logInfo('[WS] Should not connect, aborting');
      return;
    }
 
    const validToken = await getValidToken();
    if (!validToken) {
      logError('[WS] No valid token available', null);
      return;
    }
 
    const url = `${WS_BASE}/api/ws/chat?token=${validToken}`;
    logInfo('[WS] Connecting...');
    const ws = new WebSocket(url);
    wsRef.current = ws;
    lastPongRef.current = Date.now();
 
    ws.onopen = () => {
      logInfo('[WS] Connected successfully');
      reconnectCount.current = 0;
      setConnected(true);
      setWsInstance(ws);
    };
 
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        
        // Handle ping - respond with pong
        if (msg.type === 'ping') {
          logDebug('[WS] Ping received, sending pong');
          ws.send(JSON.stringify({ type: 'pong' }));
          lastPongRef.current = Date.now();
          return;
        }
 
        // Dispatch to registered handlers
        const handlers = listenerMapRef.current[msg.type];
        if (handlers) {
          logDebug('[WS] Dispatching message type:', msg.type);
          handlers.forEach(h => h(msg));
        } else {
          logDebug('[WS] No handlers for message type:', msg.type);
        }
      } catch (e) {
        logError('[WS] parse error', e);
      }
    };
 
    ws.onclose = (event) => {
      logInfo('[WS] Closed - code:', event.code, 'reason:', event.reason);
      setConnected(false);
      setWsInstance(null);
      wsRef.current = null;
      
      if (shouldConnectRef.current && reconnectCount.current < MAX_RECONNECTS) {
        reconnectCount.current += 1;
        const delay = RECONNECT_DELAY * Math.min(reconnectCount.current, 4);
        logInfo(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectCount.current}/${MAX_RECONNECTS})`);
        reconnectTimer.current = setTimeout(_connect, delay);
      } else if (reconnectCount.current >= MAX_RECONNECTS) {
        logError('[WS] Max reconnection attempts reached', null);
      }
    };
 
    ws.onerror = (err) => {
      logError('[WS] Error', err);
    };
  }, [getValidToken]);
 
  const _disconnect = useCallback(() => {
    logInfo('[WS] Disconnecting...');
    shouldConnectRef.current = false;
    clearTimeout(reconnectTimer.current);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
    setWsInstance(null);
  }, []);
 
  // Connection watchdog - reconnect if we haven't received pong in a while
  useEffect(() => {
    if (!connected) return;
 
    const watchdog = setInterval(() => {
      const timeSinceLastPong = Date.now() - lastPongRef.current;
      const threshold = 60000; // 60 seconds
      
      if (timeSinceLastPong > threshold) {
        logWarn('[WS]', `No pong received for ${Math.floor(timeSinceLastPong / 1000)} seconds, reconnecting...`);
        if (wsRef.current) {
          wsRef.current.close();
        }
      }
    }, 30000); // Check every 30 seconds
 
    return () => clearInterval(watchdog);
  }, [connected]);
 
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (isAuthenticated && token) {
      logInfo('[WS] Auth state changed: authenticated, connecting...');
      shouldConnectRef.current = true;
      reconnectCount.current = 0;
      _connect();
    } else {
      logInfo('[WS] Auth state changed: not authenticated, disconnecting...');
      _disconnect();
    }
    return () => {
      _disconnect();
    };
  }, [isAuthenticated, _connect, _disconnect]);
 
  return (
    <WSContext.Provider value={{ ws: wsInstance, connected, emit, on, off }}>
      {children}
    </WSContext.Provider>
  );
};
